import React, { useEffect, useState } from 'react'
import ContentHeader from './ContentHeader'
import { useI18n, useInsuranceCardFiles } from 'contexts/AuthContext'
import { Button } from 'antd'
import moment from 'moment-timezone'
import { isMobile } from 'utils/basic'
import UploadImage from 'components/UploadImage'
import { useUploadDocument } from 'hooks/useUploadDocument'
import { getHealthieUserId, getLocalLanguage } from 'utils/authLocalStorage'
import { ScreenKey } from './util'
import { showErrorMessage } from 'hooks/careHooks'
import { logAmplitudeEvent } from 'utils/amplitudeInit'

const InsuranceCard = (props) => {
  const { sku } = props
  const { i18n } = useI18n()
  const [isUploading, setIsUploading] = useState(false)
  const { uploadDocument } = useUploadDocument()
  const { insuranceCardFiles, setInsuranceCardFiles } = useInsuranceCardFiles()

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - Insurance - Photo Upload', {
      sku,
      language: getLocalLanguage(),
    })
  }, [sku])

  const onNext = async () => {
    const healthieUserId = getHealthieUserId()

    setIsUploading(true)

    try {
      const uploadPromises = [
        uploadDocument(insuranceCardFiles.frontFile, healthieUserId, {
          displayName: `front_of_the_card_${moment().format(
            'YYYY-MM-DD HH:mm:ss'
          )}`,
          reportType: 'Insurance Authorization',
          isPhotoId: false,
          shareWithRel: false,
        }),

        uploadDocument(insuranceCardFiles.backFile, healthieUserId, {
          displayName: `back_of_the_card_${moment().format(
            'YYYY-MM-DD HH:mm:ss'
          )}`,
          reportType: 'Insurance Authorization',
          isPhotoId: false,
          shareWithRel: false,
        }),
      ]

      const results = await Promise.all(uploadPromises)
      const isFrontFileSuccessful = results[0] && results[0].success
      const isBackFileSuccessful = results[1] && results[1].success
      const allSuccessful = isFrontFileSuccessful && isBackFileSuccessful
      if (isFrontFileSuccessful) {
        logAmplitudeEvent('Click: Insurance Photo Upload', {
          sku,
          photo: 'Front',
          language: getLocalLanguage(),
        })
      }

      if (isBackFileSuccessful) {
        logAmplitudeEvent('Click: Insurance Photo Upload', {
          sku,
          photo: 'Back',
          language: getLocalLanguage(),
        })
      }

      if (allSuccessful) {
        props.setCurrentKey(ScreenKey.INSURANCE_HOME_ADDRESS)
      } else if (!isFrontFileSuccessful) {
        showErrorMessage({ msg: 'Front of the card upload failed.' }, i18n)
      } else if (!isBackFileSuccessful) {
        showErrorMessage({ msg: 'Back of the card upload failed.' }, i18n)
      } else {
        showErrorMessage({ msg: 'Upload failed.' }, i18n)
      }
    } catch (error) {
      console.error('upload error:', error)
      showErrorMessage({ msg: 'Upload failed.' }, i18n)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div
      className='info-sec'
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <ContentHeader {...props} />
      <div className='info-content' style={{ flexGrow: 1 }}>
        <div className='title'>{i18n('Insurance card')}</div>
        <div className='subtitle'>
          {i18n('Upload images of your card for verification')}
        </div>
        <div
          className='two-grid-sec'
          style={{
            gap: '18px',
            gridTemplateColumns: 'repeat(2, calc(50% - 9px))',
          }}
        >
          <UploadImage
            {...{
              file: insuranceCardFiles.frontFile,
              setFile: (file) => {
                setInsuranceCardFiles({
                  ...insuranceCardFiles,
                  frontFile: file,
                })
              },
              description: i18n('Front of the card'),
            }}
          />
          <UploadImage
            {...{
              file: insuranceCardFiles.backFile,
              setFile: (file) => {
                setInsuranceCardFiles({ ...insuranceCardFiles, backFile: file })
              },
              description: i18n('Back of the card'),
            }}
          />
        </div>

        <div className='bottom-sec'>
          <Button
            className='btn-primary full-width-btn'
            style={isMobile() ? { width: '100%' } : {}}
            onClick={onNext}
            disabled={
              !insuranceCardFiles.frontFile ||
              !insuranceCardFiles.backFile ||
              isUploading
            }
            loading={isUploading}
          >
            {isUploading ? i18n('Uploading...') : i18n('Next')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default InsuranceCard
